<template>
    <section :id="featureId" class="feature-section">
        <container>
            <div class="feature-section-content">
                <row :reverse="right ? breakpoint : null">
                    <column :cols="12" v-bind:[breakpoint]="!$slots.default || fullWidth ? 12 : cols">
                        <p v-if="subtitleText" class="text-sub sm:text-md mb-4 text-uppercase" :class="secondary ? 'text-secondary' : 'text-primary'">{{ subtitleText }}</p>
                        <div v-if="title || langPrefix" class="flex flex-column sm:flex-row items-start sm:items-center mb-12">
                            <h2 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-text-color mb-0 leading-spaced">
                                <nuxt-link :to="featureId ? {hash: `#${featureId}`} : null">{{ title ?? $t(langPrefix + '.title') }}</nuxt-link>
                            </h2>
                            <badge v-if="badge" color="secondary" class="mt-8 sm:mt-0 sm:ml-16 text-uppercase" rounded-large spaced large>{{ badge }}</badge>
                        </div>

                        <div v-if="descriptionText" class="font-heading text-md md:text-lg leading-wide w-full mb-0" v-html="descriptionText"/>

                        <check-list v-if="featureItems.length" class="mt-24 mb-16" large>
                            <check-list-item v-for="(feature, featureKey) in featureItems" :key="featureKey" checked>
                                <span v-html="feature" class="leading-spaced"/>
                            </check-list-item>
                        </check-list>

                        <p v-if="closingText" class="text-subtext-color leading-loose mt-32" v-html="closingText"/>

                        <div v-if="$slots.text" class="mt-32">
                            <slot name="text"/>
                        </div>

                        <wrapper class="justify-center mt-32" v-if="$slots.cta" :class="ctaWrapperClass" :grow="!ctaStretch" :stretch="ctaStretch" center break-column="xs">
                            <slot name="cta"/>
                        </wrapper>
                    </column>
                    <column v-if="$slots.default" :cols="12" v-bind:[breakpoint]="fullWidth ? 12 : (12 - cols)" class="mt-32 lg:mt-0" :class="{'lg:pr-48': right && !fullWidth, 'lg:pl-48': !right && !fullWidth}">
                        <div class="flex w-full" :class="contentWrapperClass">
                            <slot/>
                        </div>
                        <wrapper class="justify-center mt-32" :class="ctaDefaultContentWrapperClass" v-if="$slots.cta" :grow="!ctaStretch" :stretch="ctaStretch" center break-column="xs">
                            <slot name="cta"/>
                        </wrapper>
                    </column>
                </row>
            </div>
            <divider v-if="divided" />
        </container>
    </section>
</template>

<script>
import {replaceAll} from "@spoferan/spoferan-ts-core";

    export default {
        name: "FeatureSection",
        props: {

            // The title to show
            title: {
                type: String,
                default: null
            },

            // The subtitle to show
            subtitle: {
                type: String,
                default: null
            },

            // The description to show
            description: {
                type: String,
                default: null
            },

			// The feature items to show
			features: {
				type: Array,
				default: null
			},

            // A label to use for the section anchor instead of the title
            label: {
                type: String,
                default: null
            },

            // The closing text to show
            closing: {
                type: String,
                default: null
            },

            // The lang key prefix for the text to show
            langPrefix: {
                type: String,
                default: null
            },

            // Aligns the feature text to the right
            right: {
                type: Boolean,
                default: false
            },

            // Whether to stretch the CTA div over the full width
            ctaStretch: {
                type: Boolean,
                default: false
            },

            // Whether to center the default slot content
            centerContent: {
                type: Boolean,
                default: true
            },

            // Whether a secondary color styling shall be applied
            secondary: {
                type: Boolean,
                default: false
            },

            divided: {
                type: Boolean,
                default: true
            },

            fullWidth: {
                type: Boolean,
                default: false
            },

            // The default width of the feature text on large screens
            cols: {
                type: [Number, String],
                default: 5
            },

            // Whether a badge with the specified text shall be placed next to the title
            badge: {
                type: String,
                default: null
            },

            // The breakpoint when to switch from a row to a column layout
            breakpoint: {
                type: String,
                default: 'lg'
            }
        },

        computed: {
            subtitleText() {
                if (this.subtitle) {
                    return this.subtitle;
                }

                if (this.langPrefix) {
					const langKey = this.langPrefix + '.subtitle'
					return this.$te(langKey) ? this.$t(langKey) : null;
                }

                return null;
            },

            descriptionText() {
                if (this.description) {
                    return this.description;
                }

                if (this.langPrefix) {
					const langKey = this.langPrefix + '.description'
					return this.$te(langKey) ? this.$t(langKey) : null;
                }

                return null;
            },

            closingText() {
                if (this.closing) {
                    return this.closing;
                }

                if (this.langPrefix) {
                    const langKey = this.langPrefix + '.closing'
                    return this.$te(langKey) ? this.$t(langKey) : null;
                }

                return null;
            },

            featureItems() {
				if (this.features) {
					return this.features;
				}

                if (this.langPrefix) {
					const langKey = this.langPrefix + '.features'
					const featureItems = this.$tm(langKey);
					return featureItems.length ? featureItems.map(label => this.$rt(label)) : [];
                }

                return [];
            },

            contentWrapperClass() {
                switch (this.breakpoint) {
                    case 'xs':
                        return 'justify-center ' + (this.right ? 'xs:justify-start' : 'xs:justify-end') + (this.centerContent ? ' xs:h-full items-center' : '');
                    case 'sm':
                        return 'justify-center ' + (this.right ? 'sm:justify-start' : 'sm:justify-end') + (this.centerContent ? ' sm:h-full items-center' : '');
                    case 'md':
                        return 'justify-center ' + (this.right ? 'md:justify-start' : 'md:justify-end') + (this.centerContent ? ' md:h-full items-center' : '');
                    case 'lg':
                        return 'justify-center ' + (this.right ? 'lg:justify-start' : 'lg:justify-end') + (this.centerContent ? ' lg:h-full items-center' : '');
                    case 'xl':
                        return 'justify-center ' + (this.right ? 'xl:justify-start' : 'xl:justify-end') + (this.centerContent ? ' xl:h-full items-center' : '');
                }
            },

            ctaWrapperClass() {
                switch (this.breakpoint) {
                    case 'xs':
                        return 'xs:justify-start' + (this.$slots.default ? ' none xs:flex' : '');
                    case 'sm':
                        return 'sm:justify-start' + (this.$slots.default ? ' none sm:flex' : '');
                    case 'md':
                        return 'md:justify-start' + (this.$slots.default ? ' none md:flex' : '');
                    case 'lg':
                        return 'lg:justify-start' + (this.$slots.default ? ' none lg:flex' : '');
                    case 'xl':
                        return 'xl:justify-start' + (this.$slots.default ? ' none xl:flex' : '');
                }
            },

            ctaDefaultContentWrapperClass() {
                switch (this.breakpoint) {
                    case 'xs':
                        return 'xs:none';
                    case 'sm':
                        return 'sm:none';
                    case 'md':
                        return 'md:none';
                    case 'lg':
                        return 'lg:none';
                    case 'xl':
                        return 'xl:none';
                }
            },

            featureId() {
				if (this.label) {
					return this.label;
				}

				if (!this.title && !this.langPrefix) {
					return null;
				}

                const title = this.title ?? this.$t(this.langPrefix + '.title');

                return replaceAll(title.toLowerCase(), [' ', ['!', '.', '?', '/', '(', ')']], ['-', ''])
            }
        }
    }
</script>
<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.app .feature-section .feature-section-content {
  padding: 64px 0;
}
@media (max-width: 1023px) {
  .app .feature-section .feature-section-content {
    padding: 24px 0;
  }
}
.app .feature-section:first-of-type .feature-section-content {
  padding-top: 0;
}
@media (max-width: 1279px) {
  .app .feature-section:first-of-type .feature-section-content {
    padding-top: 48px;
  }
}
@media (max-width: 1023px) {
  .app .feature-section:first-of-type .feature-section-content {
    padding-top: 16px;
  }
}
.app .feature-section:last-of-type {
  margin-bottom: 64px;
}
@media (max-width: 1023px) {
  .app .feature-section:last-of-type {
    margin-bottom: 24px;
  }
}
</style>
